'use client';

import type { FC, PropsWithChildren } from 'react';
import type { HeaderSummaryConfig } from '@/core/services/uniform/fetch-page-header-summary-config';
import type { FooterProps as FooterDataProps } from '@/ui/components/Footer/footer-types';
import type { HeaderProps as HeaderDataProps } from '@/ui/components/Header/header-types';
import { usePathname } from '@/core/i18n/routing';
import CountryDetectionModalWrapper from '@/ui/components/CountryDetectionModal/CountryDetectionModalWrapper';
import Footer from '@/ui/components/Footer/Footer';
import Header from '@/ui/components/Header/Header';
import ToastProvider from '@/ui/components/Toast/ToastProvider';
import {
  HeaderSummaryConfigProvider,
  HeaderSummaryNonCmsRouteReset,
} from '@/ui/context/header-summary-config';
import useRememberAccountReturnPath from '@/ui/hooks/use-remember-account-return-path';
import useRememberBookingReturnPath from '@/ui/hooks/use-remember-booking-return-path';
import useRememberCartReturnPath from '@/ui/hooks/use-remember-cart-return-path';
import cn from '@/ui/utils/cn/cn';

import $ from './MainLayout.module.scss';

type MainLayoutProps = PropsWithChildren<{
  /** Footer data from Uniform (global footer composition). */
  footerData?: FooterDataProps | null;
  /** Header data from Uniform (global header composition). */
  headerData?: HeaderDataProps | null;
  /** Header summary config from the current page composition. */
  headerSummaryConfig: HeaderSummaryConfig;
}>;

const MainLayout: FC<MainLayoutProps> = ({
  children,
  footerData,
  headerData,
  headerSummaryConfig,
}) => {
  const pathname = usePathname();
  useRememberCartReturnPath();
  useRememberAccountReturnPath();
  useRememberBookingReturnPath();
  const isConfiguratorPage = pathname.includes('/configurator');
  const isBookingPage = /(?:^|\/)booking$/.test(pathname);

  const mainContent = (
    <main
      id="main"
      className={cn($.container, isBookingPage && $.bookingMain)}
    >
      {children}
    </main>
  );

  if (isConfiguratorPage) {
    return <ToastProvider>{mainContent}</ToastProvider>;
  }

  return (
    <ToastProvider>
      <HeaderSummaryConfigProvider config={headerSummaryConfig}>
        <HeaderSummaryNonCmsRouteReset />
        <div className={$.layout}>
          <CountryDetectionModalWrapper />
          {/* A header-only wrapper breaks position:sticky; keep it on booking only. */}
          {isBookingPage ? (
            <div
              className={$.bookingDesktopHeader}
              data-testid="booking-desktop-header"
            >
              <Header headerData={headerData} />
            </div>
          ) : (
            <Header headerData={headerData} />
          )}
          {mainContent}
          <div
            className={isBookingPage ? $.bookingDesktopFooter : undefined}
          >
            <Footer footerData={footerData} />
          </div>
        </div>
      </HeaderSummaryConfigProvider>
    </ToastProvider>
  );
};

export default MainLayout;
